import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';

export interface AdvancedSearchState {
  entityType: string;
  countPerPlatform: number;
  includeNews: boolean;
  includeMarketData: boolean;
  sentimentAnalysis: boolean;
  trendAnalysis: boolean;
}

export const DEFAULT_ADVANCED_STATE: AdvancedSearchState = {
  entityType: 'topic',
  countPerPlatform: 50,
  includeNews: true,
  includeMarketData: true,
  sentimentAnalysis: true,
  trendAnalysis: true,
};

const ENTITY_TYPES = [
  { value: 'company', label: 'Company' },
  { value: 'personality', label: 'Personality' },
  { value: 'topic', label: 'Topic' },
  { value: 'location', label: 'Location' },
];

interface AdvancedSearchPanelProps {
  value: AdvancedSearchState;
  onChange: (next: AdvancedSearchState) => void;
  searchHistory: Array<{ query: string; platforms: string[] }>;
  onPickHistory: (query: string, platforms: string[]) => void;
}

export const AdvancedSearchPanel: React.FC<AdvancedSearchPanelProps> = ({ value, onChange, searchHistory, onPickHistory }) => {
  const set = <K extends keyof AdvancedSearchState>(k: K, v: AdvancedSearchState[K]) => onChange({ ...value, [k]: v });

  return (
    <div className="px-4 py-3 space-y-3" style={{ background: '#0a0a0a', borderTop: '1px solid #1a1a1a' }}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-600 font-mono uppercase">Entity type:</span>
          <Select value={value.entityType} onValueChange={v => set('entityType', v)}>
            <SelectTrigger className="h-7 text-[11px] w-32" style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
              {ENTITY_TYPES.map(t => <SelectItem key={t.value} value={t.value} style={{ color: '#e5e7eb' }}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-gray-600 font-mono uppercase">Count/platform:</span>
          <Input type="number" min={10} max={500} value={value.countPerPlatform}
            onChange={e => set('countPerPlatform', Math.max(10, Math.min(500, Number(e.target.value) || 50)))}
            className="h-7 text-[11px] w-20" style={{ background: '#111', border: '1px solid #222', color: '#e5e7eb' }} />
        </div>
      </div>

      <div className="flex items-center gap-4 flex-wrap">
        {([
          ['includeNews', 'News articles'],
          ['includeMarketData', 'Market data'],
          ['sentimentAnalysis', 'AI sentiment'],
          ['trendAnalysis', 'AI trends'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-1.5 text-[11px] text-gray-500 cursor-pointer">
            <input type="checkbox" checked={value[key]} onChange={e => set(key, e.target.checked)} className="w-3 h-3 accent-[#00ff00]" />
            {label}
          </label>
        ))}
      </div>

      {searchHistory.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-gray-600 font-mono uppercase">Recent:</span>
          {searchHistory.slice(0, 6).map((h, i) => (
            <button key={i} onClick={() => onPickHistory(h.query, h.platforms)}
              className="text-[10px] px-2 py-0.5 rounded font-mono"
              style={{ background: '#111', border: '1px solid #222', color: '#6b7280' }}>
              {h.query}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchPanel;
